/**
 * Blog post data store.
 *
 * To publish a new post, add an object to the POSTS array at the top
 * (newest first). The BlogListPage and BlogPostPage components render
 * everything automatically from this data — no JSX needed.
 *
 * Content schema per post:
 *   slug         string   — URL path segment, e.g. "heat-pump-rebate-victoria"
 *   title        string   — H1 / <title> tag
 *   date         string   — ISO date, e.g. "2026-04-15"
 *   readTime     string   — e.g. "5 min read"
 *   category     string   — displayed as eyebrow label
 *   description  string   — meta description (≤160 chars)
 *   keywords     string   — comma-separated SEO keywords
 *   intro        string   — lead paragraph shown under the hero title
 *   sections     array    — each item: { heading, paragraphs[], list?, tip?, note? }
 *   faq          array    — each item: { q, a }
 */

export const POSTS = [
  /* ─────────────────────────────────────────────────────────────
     POST 8 — Replace Gas Heater VEU Rebate (2026-04-22)
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'replace-gas-heater-reverse-cycle-ac-rebate-victoria',
    title: 'Replace Your Gas Heater With a Reverse-Cycle AC: VEU Rebate Guide Victoria',
    date: '2026-04-22',
    readTime: '6 min read',
    category: 'Gas Heater Replacement',
    description: 'Victorian homeowners can access up to $7,000 in VEU rebates when replacing a gas heater with a reverse-cycle air conditioner. Find out what qualifies and how to claim.',
    keywords: 'replace gas heater Victoria rebate, gas heater to reverse cycle AC VEU, ditch gas heater Victoria, gas heater replacement rebate Melbourne, VEU gas ducted heating replacement, replace gas wall heater reverse cycle Victoria',
    intro: 'With Victorian energy prices rising and the state government committed to electrification, now is an ideal time to replace your gas heater with a modern reverse-cycle air conditioner — and the VEU program will help cover the cost. Gas heater replacement attracts some of the largest rebates available under the Victorian Energy Upgrades program, with eligible households able to access up to $7,000 off the price of a new system.',
    sections: [
      {
        heading: 'Why Replace a Gas Heater Now?',
        paragraphs: [
          'Gas prices in Victoria have increased significantly in recent years, and the long-term outlook for gas costs remains unfavourable. At the same time, electricity from the grid is becoming progressively cleaner as Victoria transitions to renewables — meaning an electric reverse-cycle system gets greener over time.',
          'Reverse-cycle air conditioners are also significantly more efficient than gas heaters. A modern unit delivers 3–5 units of heating for every unit of electricity consumed — an efficiency of 300–500%, compared to at best 90% for even the most efficient gas systems.',
          'And right now, the VEU program makes the switch financially accessible: the government rebate for replacing gas heating is among the highest available under the entire scheme.',
        ],
        tip: 'April is an ideal time to act — you will have your new system installed and running before the peak of winter, and you avoid the seasonal rush that hits in May and June.',
      },
      {
        heading: 'What Is the VEU Rebate for Gas Heater Replacement?',
        paragraphs: [
          'Under the Victorian Energy Upgrades (VEU) program, installing a reverse-cycle air conditioner to replace gas ducted heating can attract a rebate of up to $7,000, depending on the size and type of system installed and prevailing VEEC prices.',
          'For gas wall heaters and gas space heaters being replaced with a split-system reverse-cycle unit, typical rebate values range from $500 to $3,000. In all cases, the rebate is applied as a direct discount on installation day — no forms, no waiting.',
        ],
      },
      {
        heading: 'Which Gas Heaters Qualify for the VEU Rebate?',
        paragraphs: [
          'The following gas heating systems are eligible "before" appliances under the VEU program when replacing with a reverse-cycle air conditioner:',
        ],
        list: {
          items: [
            'Gas ducted heating systems (central duct and zoned systems) — highest rebate value',
            'Gas wall heaters (flued and unflued)',
            'Gas space heaters',
            'Gas log fires used as primary heating',
          ],
          ordered: false,
        },
        note: 'Electric heaters, older reverse-cycle AC units, and evaporative coolers may also qualify as "before" appliances under different VEU scenarios. Book a free assessment to confirm your specific situation.',
      },
      {
        heading: 'What Reverse-Cycle System Will Replace It?',
        paragraphs: [
          'A range of reverse-cycle air conditioning systems qualify under VEU as the replacement appliance, provided they appear on the ESC\'s approved product list:',
        ],
        list: {
          items: [
            'Wall-mounted split systems (single zone) — suitable for individual rooms or open-plan living areas',
            'Multi-head split systems (multi-zone) — one outdoor unit, multiple indoor units for whole-home coverage',
            'Ducted reverse-cycle systems — for homes requiring whole-home heating and cooling via ceiling vents',
          ],
          ordered: false,
        },
        tip: 'For most Victorian homes replacing a central gas ducted system, a ducted reverse-cycle system provides the closest like-for-like experience — with the bonus of cooling capability in summer.',
      },
      {
        heading: 'Gas Ducted vs Split System: Which Should You Choose?',
        paragraphs: [
          'Choosing between a ducted and split-system replacement comes down to your home layout, existing ductwork, and budget after the rebate.',
          'If your home already has ducted infrastructure in reasonable condition, a ducted reverse-cycle system reuses those ducts and maintains whole-home climate control from a central unit. The VEU rebate for ducted systems replacing gas ducted heating is typically the largest available — often $3,000–$7,000.',
          'If you are replacing individual gas wall heaters in specific rooms, a split system for each zone is often more practical and cost-effective. Modern high-wall split systems are quiet, efficient, and available in inverter technology that precisely matches output to your heating or cooling demand.',
        ],
      },
      {
        heading: 'What About the Gas Connection?',
        paragraphs: [
          'Once your new reverse-cycle system is installed, you may choose to remain connected to gas for other appliances (cooktop, oven) or to disconnect gas entirely — depending on your longer-term electrification goals.',
          'Disconnecting from gas can save Victorian households a further $300–$500 per year in gas supply (daily) charges alone, even before counting the gas you no longer use. The AGL, Origin, or Jemena gas disconnection process is straightforward and Movement Green can advise on the right sequence if you are considering full electrification.',
        ],
      },
      {
        heading: 'How to Claim Your Gas Heater Replacement Rebate',
        paragraphs: [
          'The process is simple and the paperwork is handled for you. Here is what to expect:',
        ],
        list: {
          items: [
            'Book a free home energy assessment with Movement Green',
            'We inspect your existing gas heating and confirm VEU eligibility',
            'You receive a written quote showing the full system price, the VEU rebate applied, and the amount you pay',
            'Confirm your chosen system and preferred installation date',
            'Our team installs your new reverse-cycle system — typically in one day',
            'Your invoice shows the rebate applied directly — you pay only the discounted price',
          ],
          ordered: true,
        },
      },
    ],
    faq: [
      {
        q: 'How much is the rebate for replacing gas ducted heating with reverse-cycle AC?',
        a: 'The VEU rebate for replacing gas ducted heating with a reverse-cycle system can reach up to $7,000 for larger capacity systems. The exact amount depends on the system size (kW capacity), the specific unit selected, and current VEEC market prices. Book a free assessment with Movement Green for an exact figure.',
      },
      {
        q: 'Do I need to remove the gas ducted system before getting the rebate?',
        a: 'Yes. To claim the VEU rebate, the existing gas heating appliance must be decommissioned as part of the installation. As a VEU Accredited Provider, Movement Green handles the safe removal and disposal of the old system on installation day.',
      },
      {
        q: 'Can I keep some gas appliances and still get the VEU rebate for replacing my heater?',
        a: 'Yes. The VEU rebate applies to the specific appliance being replaced (your gas heater). You do not need to disconnect all gas from your home. You can replace your gas heater with a reverse-cycle AC and still retain a gas cooktop or other gas appliances.',
      },
      {
        q: 'Is a reverse-cycle air conditioner good enough to heat a Victorian home in winter?',
        a: 'Yes. Modern reverse-cycle systems are highly effective in Victorian winters. They are rated to operate efficiently down to -15°C, well below any temperature Melbourne or regional Victoria will experience. Many Victorian households that switch from gas report being warmer in winter, not colder.',
      },
      {
        q: 'Will my energy bills go up if I switch from gas heating to electric?',
        a: 'For most Victorian households, no — especially given the efficiency advantage of reverse-cycle systems. A modern reverse-cycle unit is 300–500% efficient, meaning it produces more heat energy than the electricity it consumes. Compare this to gas which is at best 90% efficient. Combined with removing the daily gas supply charge, most households see overall energy costs decrease after switching.',
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 7 — How to Get Your VEU Rebate (newest first keeps feed fresh)
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'how-to-claim-veu-rebate-victoria',
    title: 'How to Get Your VEU Rebate in Victoria: A Step-by-Step Guide',
    date: '2026-04-21',
    readTime: '5 min read',
    category: 'How-To Guides',
    description: 'A step-by-step guide to claiming your Victorian Energy Upgrades (VEU) rebate in Victoria — from booking your free assessment to installation day and beyond.',
    keywords: 'how to get VEU rebate Victoria, claim VEU rebate, VEU rebate process Victoria, how does VEU rebate work, apply for VEU rebate',
    intro: 'Claiming a government rebate on energy-efficient appliances sounds complicated — but under the Victorian Energy Upgrades (VEU) program, the process has been designed to be as simple as possible. Here is exactly what happens, step by step.',
    sections: [
      {
        heading: 'Step 1: Book a Free Home Energy Assessment',
        paragraphs: [
          'The first step is booking a free, no-obligation energy assessment with a VEU Accredited Provider like Movement Green. Our team visits your home and assesses your existing appliances — hot water system, heating and cooling units, lighting — to identify every upgrade that qualifies for a government rebate.',
          'There is no cost for the assessment, and no obligation to proceed. It typically takes 30–60 minutes.',
        ],
      },
      {
        heading: 'Step 2: Receive Your Tailored Upgrade Plan',
        paragraphs: [
          'After the assessment, you receive a personalised upgrade plan listing all eligible upgrades for your property, the estimated rebate value for each, and projected ongoing energy savings.',
          'Your home may qualify for multiple upgrades at the same time — for example, a heat pump hot water system, a reverse-cycle air conditioner, and LED lighting. Combining upgrades means more total rebate value and a single installation visit.',
        ],
      },
      {
        heading: 'Step 3: Choose Your Upgrades and Confirm Installation',
        paragraphs: [
          'You decide which upgrades to proceed with. There is no pressure to take every recommendation — many customers start with one upgrade and return for others later.',
          'Once you confirm, we schedule installation at a time that suits you. For most upgrades, a single day is all that is needed.',
        ],
      },
      {
        heading: 'Step 4: Installation by a VEU Accredited Provider',
        paragraphs: [
          'VEU installations must be delivered by an Accredited Person (AP) — a business accredited by the Essential Services Commission (ESC) of Victoria. Movement Green is a VEU Accredited Provider, so every installation we deliver meets the program\'s strict quality and compliance standards.',
          'On installation day, the team arrives, completes the work cleanly and efficiently, and walks you through the new appliance before leaving. Hot water and reverse-cycle AC installs typically take 3–6 hours. LED lighting upgrades are usually done in 1–2 hours.',
        ],
      },
      {
        heading: 'Step 5: Your Rebate Is Applied — No Paperwork Required',
        paragraphs: [
          'Here is where the VEU program is different from many other rebate schemes: the rebate is applied as a point-of-sale discount. You do not pay full price and then claim money back later. The value of the government rebate is deducted directly from your invoice — so you only pay the reduced amount on the day.',
          'As the Accredited Person, Movement Green handles all certificate creation and regulatory paperwork. You receive a tax invoice showing the full product price, the VEU rebate applied, and the final amount you pay.',
        ],
        tip: 'You never need to lodge a claim, fill out a government form, or chase a rebate payment. The discount is instant.',
      },
      {
        heading: 'Step 6: Start Saving on Your Energy Bills',
        paragraphs: [
          'With your new energy-efficient appliance installed, the savings begin immediately. Most households see a noticeable reduction in their electricity or gas bills within the first billing cycle.',
          'Heat pump hot water systems typically cut hot water energy costs by 60–70%. Reverse-cycle air conditioners use up to 50% less energy than older units. LED lighting consumes around 80% less electricity than halogen globes.',
        ],
      },
      {
        heading: 'What Documents Do You Need?',
        paragraphs: [
          'The paperwork burden on the homeowner is minimal. You will typically need to provide:',
        ],
        list: {
          items: [
            'Proof of address (utility bill or council rates notice)',
            'Proof that the existing appliance is being replaced (not a brand-new installation in a new property, in most cases)',
            'Your signature on the installation paperwork confirming you agree to the upgrade',
          ],
          ordered: false,
        },
      },
    ],
    faq: [
      {
        q: 'Do I need to pay anything upfront for a VEU upgrade?',
        a: 'For many upgrades the rebate covers the full cost, meaning no out-of-pocket expense. For others, particularly reverse-cycle AC, there may be a co-payment depending on the unit selected. Your upgrade plan will show the exact amount before you commit.',
      },
      {
        q: 'How long does the VEU rebate process take from start to finish?',
        a: 'From booking your assessment to completed installation is typically 1–2 weeks, depending on scheduling. The assessment itself takes under an hour, and most installations are completed in a single day.',
      },
      {
        q: 'Can renters claim VEU rebates?',
        a: 'Yes, renters can access VEU upgrades, but the property owner (landlord) generally needs to consent since the upgrade becomes a fixture of the property. Contact us and we can help facilitate this conversation.',
      },
      {
        q: 'What if my existing appliance is still working — can I still get a rebate?',
        a: 'In most cases, yes. The VEU program is designed to encourage replacement of inefficient appliances, even if they are still operational. The key requirement is that the existing appliance meets the eligibility criteria for the specific upgrade type.',
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 6 — VEU Eligibility
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'who-qualifies-for-veu-rebates-victoria',
    title: 'Who Qualifies for Victorian Energy Upgrades (VEU) Rebates? Eligibility Explained',
    date: '2026-04-20',
    readTime: '5 min read',
    category: 'VEU Eligibility',
    description: 'Find out who qualifies for Victorian Energy Upgrades (VEU) rebates in Victoria — homeowners, renters, landlords, and businesses — and what conditions apply.',
    keywords: 'VEU eligibility Victoria, who qualifies VEU, Victorian Energy Upgrades eligibility, VEU rebate eligibility Melbourne, renter VEU rebate, landlord VEU Victoria',
    intro: 'One of the most common questions we hear is: "Am I eligible for a VEU rebate?" The short answer is: most Victorian households and many businesses are. Here is everything you need to know about eligibility under the Victorian Energy Upgrades program.',
    sections: [
      {
        heading: 'The Basic Eligibility Rule',
        paragraphs: [
          'The Victorian Energy Upgrades (VEU) program is available to Victorian residents, businesses, and community organisations who are replacing or upgrading existing eligible appliances. The property must be located in Victoria, and the upgrade must involve the installation of a VEU-approved product.',
          'Unlike some government rebate schemes, the VEU program is not means-tested. You do not need to be on a low income or hold a concession card to qualify — the rebate is available to all eligible Victorian households regardless of income.',
        ],
      },
      {
        heading: 'Homeowners',
        paragraphs: [
          'If you own your home in Victoria and have an existing eligible appliance — such as a gas or electric hot water system, an older air conditioner, or halogen or fluorescent lighting — you are very likely to qualify for one or more VEU rebates.',
          'Owner-occupiers are the most straightforward case. You control the property, you benefit directly from the lower energy bills, and there is no third-party approval needed.',
        ],
        tip: 'Even if your existing appliance is still working, you may still be able to replace it under the VEU program. You do not need to wait for it to break down.',
      },
      {
        heading: 'Renters and Tenants',
        paragraphs: [
          'Renters can access VEU upgrades, but because the installed appliance becomes a fixture of the property, the property owner (landlord) generally needs to provide consent. In practice, this often means the upgrade benefits both parties — the tenant gets a more efficient appliance and lower bills, while the landlord improves the property\'s appeal and energy rating.',
          'We recommend discussing the upgrade with your landlord or property manager. In many cases, landlords are supportive because the installation is free or heavily subsidised. Movement Green can provide information to help you have that conversation.',
        ],
      },
      {
        heading: 'Landlords and Property Investors',
        paragraphs: [
          'Rental property owners are eligible to access VEU rebates for their investment properties. Upgrading to energy-efficient appliances can improve the property\'s energy star rating, increase rental appeal, and reduce the risk of appliance failure and emergency repair costs.',
          'For landlords with multiple properties, each property can be assessed and upgraded independently. Portfolio owners often work with Movement Green to systematically upgrade their properties over time.',
        ],
      },
      {
        heading: 'Businesses and Commercial Properties',
        paragraphs: [
          'Victorian businesses, including offices, retail stores, warehouses, and hospitality venues, are eligible for VEU rebates on eligible upgrades. LED lighting upgrades are particularly popular for commercial properties given the scale of lighting in these environments.',
          'Businesses can access significant rebates on commercial-grade reverse-cycle air conditioning systems, heat pump hot water for staff facilities, and comprehensive LED lighting overhauls.',
        ],
      },
      {
        heading: 'Community Organisations and Schools',
        paragraphs: [
          'Registered community organisations, not-for-profits, schools, and similar entities operating in Victoria are also eligible for VEU rebates. These organisations often have large lighting loads and older HVAC equipment, making them strong candidates for significant savings.',
        ],
      },
      {
        heading: 'What Makes a Property Ineligible?',
        paragraphs: [
          'There are some situations where a property or upgrade may not qualify:',
        ],
        list: {
          items: [
            'New builds where there is no existing appliance being replaced (for most upgrade types)',
            'Properties located outside of Victoria',
            'Installations that do not use VEU-approved products (products must appear on the ESC\'s approved product list)',
            'Situations where the existing appliance does not meet the eligibility criteria for that upgrade type',
          ],
          ordered: false,
        },
      },
    ],
    faq: [
      {
        q: 'Do I need to be a homeowner to get a VEU rebate?',
        a: 'No. Renters can access VEU rebates with landlord consent. Landlords can also access rebates for rental properties. The program is open to homeowners, tenants, landlords, businesses, and community organisations across Victoria.',
      },
      {
        q: 'Is there an income test for the VEU program?',
        a: 'No. The VEU program is not means-tested. You do not need to be on a low income, hold a concession card, or meet any income threshold to qualify. The program is available to all eligible Victorian households and businesses.',
      },
      {
        q: 'My hot water system still works — can I still upgrade under VEU?',
        a: 'Yes. You do not need to wait until your appliance breaks down. The VEU program is designed to encourage proactive replacement of inefficient appliances, even when they are still functional. Book a free assessment to confirm your specific situation.',
      },
      {
        q: 'Can I get VEU rebates for a holiday home or investment property?',
        a: 'Yes, provided the property is located in Victoria and the upgrade involves replacing eligible existing appliances with VEU-approved products.',
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 5 — Solar Battery Storage (Solar Homes Program — NOT VEU)
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'solar-battery-storage-rebate-victoria',
    title: 'Solar Battery Storage Rebate Victoria 2026: Solar Homes Program Explained',
    date: '2026-04-19',
    readTime: '6 min read',
    category: 'Solar Battery',
    description: 'Solar battery storage rebates in Victoria are available through the Solar Homes Program — a separate scheme from VEU. Learn how it works, who qualifies, and how to combine it with VEU upgrades.',
    keywords: 'solar battery rebate Victoria, solar battery storage Victoria, Solar Homes Program battery rebate, home battery storage Victoria rebate, solar battery government rebate Melbourne 2025, Solar Victoria battery rebate',
    intro: 'If you are looking for a government rebate on solar battery storage in Victoria, it is important to understand which scheme covers it. Solar battery storage is NOT part of the Victorian Energy Upgrades (VEU) program — it is supported through a separate Victorian Government initiative called the Solar Homes Program, administered by Solar Victoria. Here is what you need to know.',
    sections: [
      {
        heading: 'Solar Battery Is Not Under VEU',
        paragraphs: [
          'The Victorian Energy Upgrades (VEU) program — which covers heat pump hot water systems, reverse-cycle air conditioners, LED lighting, and commercial solar — does not include residential solar battery storage.',
          'Solar battery storage for homes is a separate scheme altogether. It is funded through the Solar Homes Program, administered by Solar Victoria (a body under the Department of Energy, Environment and Climate Action). The two programs operate independently, with different eligibility rules, application processes, and administering bodies.',
        ],
        note: 'If someone tells you that solar battery storage is VEU-eligible, this is incorrect. Always check which program applies before proceeding.',
      },
      {
        heading: 'The Solar Homes Program: How Battery Rebates Work',
        paragraphs: [
          'The Solar Homes Program provides rebates for eligible Victorian households on solar panel systems, solar hot water systems, and solar battery storage. The battery storage rebate is designed for households that already have a solar PV system installed and want to add battery storage.',
          'Unlike VEU rebates — which are applied as a point-of-sale discount with no paperwork required — Solar Homes Program rebates are typically claimed through the Solar Victoria portal. Installations must be carried out by a Clean Energy Council-accredited installer.',
        ],
        tip: 'Visit the Solar Victoria website (solar.vic.gov.au) for up-to-date rebate amounts, eligibility criteria, and approved installer lists for battery storage.',
      },
      {
        heading: 'Who Qualifies for the Solar Battery Rebate?',
        paragraphs: [
          'Eligibility criteria for the Solar Homes battery rebate include:',
        ],
        list: {
          items: [
            'The property must be located in Victoria',
            'You must be an owner-occupier (rental properties may have different rules)',
            'A solar PV system must already be installed at the property',
            'The property must meet the Solar Victoria income or property value eligibility threshold (check solar.vic.gov.au for current limits)',
            'The battery must be installed by a Clean Energy Council-accredited installer',
          ],
          ordered: false,
        },
      },
      {
        heading: 'VEU vs. Solar Homes Program: Key Differences',
        paragraphs: [
          'Understanding the difference between VEU and the Solar Homes Program helps you make the most of both schemes.',
        ],
        list: {
          items: [
            'VEU covers: heat pump hot water, reverse-cycle AC, LED lighting, commercial solar. Rebate applied at point of sale.',
            'Solar Homes Program covers: residential solar panels, solar battery storage. Claim lodged through the Solar Victoria portal.',
            'VEU is income-neutral (no means test). Solar Homes has eligibility criteria including a property value cap.',
            'VEU installations use ESC-accredited Accredited Persons. Solar Homes uses CEC-accredited installers.',
          ],
          ordered: false,
        },
      },
      {
        heading: 'Combining Solar Homes Battery With VEU Upgrades',
        paragraphs: [
          'The good news is that the two schemes can be used together. There is nothing stopping you from accessing a Solar Homes battery rebate at the same time as claiming VEU rebates for your heat pump, reverse-cycle AC, or LED lighting.',
          'In fact, pairing battery storage with energy-efficient VEU-upgraded appliances is an excellent strategy for reducing your overall household energy costs. A battery stores the solar energy you generate during the day, while VEU upgrades reduce how much energy your home needs in the first place.',
          'Movement Green helps Victorian homeowners identify and access all applicable VEU rebates. For Solar Homes Program applications, we recommend visiting the Solar Victoria website directly or engaging a CEC-accredited solar installer.',
        ],
      },
      {
        heading: 'What VEU Upgrades Can I Combine With Battery Storage?',
        paragraphs: [
          'While Movement Green specialises in VEU-eligible upgrades rather than battery storage, the two sets of upgrades complement each other well. The following VEU upgrades pair effectively with a home battery system:',
        ],
        list: {
          items: [
            'Heat pump hot water system — runs efficiently off stored solar energy during off-peak hours',
            'Reverse-cycle air conditioner — high-efficiency unit reduces the load on your battery',
            'LED lighting upgrade — dramatically reduces your baseline electricity draw, extending battery runtime',
          ],
          ordered: false,
        },
        tip: 'Book a free VEU assessment with Movement Green to identify every heat pump, AC, and LED rebate your home qualifies for — then tackle solar battery separately through Solar Victoria.',
      },
    ],
    faq: [
      {
        q: 'Is solar battery storage covered by the VEU program?',
        a: 'No. Solar battery storage for residential properties is not eligible under the Victorian Energy Upgrades (VEU) program. It is covered by a separate scheme — the Solar Homes Program, administered by Solar Victoria. The VEU program covers heat pump hot water, reverse-cycle air conditioning, LED lighting, and commercial solar.',
      },
      {
        q: 'Which Victorian Government program covers solar battery storage?',
        a: 'The Solar Homes Program, run by Solar Victoria (solar.vic.gov.au), provides rebates for residential solar battery storage in Victoria. This is entirely separate from the VEU program run by the Essential Services Commission (ESC).',
      },
      {
        q: 'Can I get both a Solar Homes battery rebate and VEU rebates at the same time?',
        a: 'Yes. The two programs are independent and can be used together. You can access VEU rebates for heat pumps, reverse-cycle AC, and LED lighting through Movement Green, and separately apply for a Solar Homes battery rebate through Solar Victoria.',
      },
      {
        q: 'Does commercial solar qualify under VEU?',
        a: 'Yes. Commercial solar PV installations for Victorian businesses are eligible under the VEU program. This is different from residential home solar and residential battery storage, which are not covered by VEU. Contact Movement Green if you are a business interested in commercial solar under VEU.',
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 4 — LED Lighting
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'led-lighting-upgrade-veu-rebate-victoria',
    title: 'LED Lighting Upgrade Victoria: Claim Your VEU Government Rebate',
    date: '2026-04-18',
    readTime: '4 min read',
    category: 'LED Lighting',
    description: 'LED lighting upgrades are one of the most accessible VEU rebates in Victoria. Find out what qualifies, how much you save, and how to claim your government rebate.',
    keywords: 'LED lighting upgrade VEU, LED lighting rebate Victoria, government rebate LED lighting Victoria, LED lighting upgrade Melbourne, halogen to LED rebate VEU, fluorescent lighting upgrade Victoria',
    intro: 'Replacing halogen or fluorescent lighting with high-efficiency LED fittings is one of the quickest, lowest-hassle upgrades available under the Victorian Energy Upgrades (VEU) program — and for many households and businesses, it is also one of the most impactful on their power bill.',
    sections: [
      {
        heading: 'What LED Lighting Qualifies Under VEU?',
        paragraphs: [
          'The VEU program covers the replacement of inefficient existing lighting with VEU-approved LED products. Eligible lighting types include:',
        ],
        list: {
          items: [
            'Halogen downlights (the round recessed globes found in most Victorian homes)',
            'Fluorescent tube lighting (common in kitchens, laundries, garages, and commercial spaces)',
            'T8 and T5 fluorescent battens',
            'Compact fluorescent lamps (CFLs) in some configurations',
          ],
          ordered: false,
        },
      },
      {
        heading: 'How Much Energy Do LED Lights Save?',
        paragraphs: [
          'LED lighting uses approximately 75–80% less electricity than halogen or fluorescent equivalents to produce the same amount of light. For a typical Victorian home with 15–20 halogen downlights, switching to LED can reduce lighting energy consumption by hundreds of kilowatt-hours per year.',
          'At average Victorian electricity rates, that translates to savings of $100–$300 per year for a residential property, depending on the number of fittings and usage patterns. For businesses and commercial properties with larger lighting loads, annual savings can run into thousands of dollars.',
        ],
        tip: 'LED lights also last significantly longer — rated at 15,000–50,000 hours compared to 2,000 hours for a typical halogen globe. That means fewer replacements and less maintenance cost over time.',
      },
      {
        heading: 'How Much Is the VEU LED Lighting Rebate?',
        paragraphs: [
          'The rebate value for LED lighting under the VEU program depends on the number of fittings being replaced, the type of existing lighting, and current VEEC prices. For many residential properties, the rebate covers the full cost of supply and installation — meaning the upgrade is effectively free to the homeowner.',
          'For commercial properties with larger lighting loads, the rebate scale can deliver substantial savings on what would otherwise be a significant capital expenditure.',
        ],
      },
      {
        heading: 'The Installation Process',
        paragraphs: [
          'LED lighting installations are fast. A trained installer can typically replace all qualifying fittings in a standard Victorian home in 1–3 hours, with minimal disruption. The process involves:',
        ],
        list: {
          items: [
            'Removal of existing halogen or fluorescent fittings',
            'Installation of VEU-approved LED downlights or tubes',
            'Testing all fittings for correct operation',
            'Clean-up and sign-off on the installation documentation',
          ],
          ordered: true,
        },
      },
      {
        heading: 'Can I Combine LED Lighting With Other VEU Upgrades?',
        paragraphs: [
          'Absolutely. LED lighting is commonly bundled with heat pump hot water and reverse-cycle AC upgrades in the same visit. Combining upgrades is efficient, maximises your total rebate value, and means only one installation appointment to coordinate.',
          'Movement Green will assess your home for all eligible upgrades and can schedule everything in a single day where possible.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is the LED lighting upgrade really free under VEU?',
        a: 'For many Victorian homes, yes — the VEU rebate covers the full cost of supply and installation of LED downlights replacing halogens. The exact coverage depends on the number of fittings and current VEEC prices. A free assessment will confirm whether your upgrade will be fully subsidised or involve a small co-payment.',
      },
      {
        q: 'How many lights do I need to replace to qualify?',
        a: 'There is no minimum number of fittings required to access the VEU LED rebate, but practically speaking most properties benefit from replacing all qualifying fittings in one visit to maximise rebate value and minimise disruption.',
      },
      {
        q: 'Do LED lights really look the same as halogens?',
        a: 'Modern VEU-approved LED downlights are designed to match the warm colour temperature and beam angle of traditional halogen globes. Most customers cannot tell the difference in appearance — but they notice the difference on their electricity bill.',
      },
      {
        q: 'Can businesses claim the LED lighting rebate?',
        a: 'Yes. Victorian businesses, offices, retail spaces, and warehouses with eligible fluorescent or halogen lighting can access VEU rebates on LED upgrades. Commercial LED upgrades can be particularly valuable given the scale of lighting involved.',
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 3 — Reverse-Cycle AC Rebate
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'reverse-cycle-air-conditioner-rebate-victoria',
    title: 'Reverse-Cycle Air Conditioner Rebate Victoria: Up to $7,000 Under VEU',
    date: '2026-04-17',
    readTime: '6 min read',
    category: 'Air Conditioning',
    description: 'Victorian homeowners can access up to $7,000 in government rebates on reverse-cycle air conditioner installation under the VEU program. Learn what qualifies and how to claim.',
    keywords: 'reverse cycle air conditioner rebate Victoria, AC rebate VEU, air conditioner government rebate Melbourne, reverse cycle AC rebate 2025, VEU air conditioner rebate, split system AC rebate Victoria',
    intro: 'A reverse-cycle air conditioner is the most energy-efficient way to heat and cool your Victorian home — and right now, Victorian homeowners can access up to $7,000 in government rebates on a new system under the Victorian Energy Upgrades (VEU) program. Here is everything you need to know about the AC rebate, what qualifies, and how to claim.',
    sections: [
      {
        heading: 'What Is the Reverse-Cycle AC Rebate Under VEU?',
        paragraphs: [
          'The Victorian Energy Upgrades (VEU) program provides a substantial government rebate on the installation of high-efficiency reverse-cycle air conditioning systems. The rebate is funded through the creation of Victorian Energy Efficiency Certificates (VEECs), which are transferred to energy retailers in exchange for the discount you receive at installation.',
          'The rebate is applied as a direct point-of-sale discount — you pay the reduced price on installation day. No forms, no waiting for a rebate cheque.',
        ],
      },
      {
        heading: 'How Much Is the Reverse-Cycle AC Rebate?',
        paragraphs: [
          'The rebate value depends on the size (capacity) of the system installed, the type of existing heating or cooling being replaced, and prevailing VEEC prices. For larger systems replacing gas ducted heating, rebates can reach up to $7,000.',
          'Even for smaller split-system installations, the rebate is typically $500–$2,500, making a significant difference to the out-of-pocket cost.',
        ],
        tip: 'The highest rebates are available when replacing gas ducted heating with a reverse-cycle system. Book a free assessment to get your property\'s exact rebate figure.',
      },
      {
        heading: 'What Types of Systems Qualify?',
        paragraphs: [
          'The following reverse-cycle air conditioning systems are eligible under the VEU program, provided they appear on the ESC\'s approved product list:',
        ],
        list: {
          items: [
            'Single-zone wall-mounted split systems',
            'Multi-zone (multi-head) split systems',
            'Ducted reverse-cycle systems',
          ],
          ordered: false,
        },
      },
      {
        heading: 'What Are You Replacing? (The "Before" Requirement)',
        paragraphs: [
          'To qualify for the VEU reverse-cycle AC rebate, you typically need to be replacing an existing less-efficient heating or cooling appliance. Eligible "before" scenarios include:',
        ],
        list: {
          items: [
            'Gas ducted heating systems (highest rebate value)',
            'Gas wall heaters or space heaters',
            'Old or inefficient electric heaters',
            'Older, lower-efficiency reverse-cycle systems',
          ],
          ordered: false,
        },
      },
      {
        heading: 'Why Reverse-Cycle? The Efficiency Advantage',
        paragraphs: [
          'Reverse-cycle air conditioners do not generate heat — they move it. By extracting heat energy from outdoor air and transferring it inside, a modern reverse-cycle system delivers 3–5 units of heating for every unit of electricity consumed. This makes it 300–500% efficient, compared to 100% for a standard electric heater.',
          'In cooling mode, the process reverses — heat is extracted from inside and pumped outside. The same unit handles both functions, replacing the need for separate heating and cooling appliances.',
          'For Victorian winters, modern reverse-cycle units continue to operate effectively down to -15°C — well below any temperature Melbourne or regional Victoria will experience.',
        ],
      },
      {
        heading: 'How to Claim the Reverse-Cycle AC Rebate',
        paragraphs: [
          'The process is simple: book a free assessment with Movement Green, we confirm your eligibility and the exact rebate value, select a VEU-approved system, and our team installs it — with the rebate applied directly to your invoice by Movement Green as a VEU Accredited Provider.',
          'Installation of a standard split system typically takes half a day. For ducted systems, allow a full day.',
        ],
      },
    ],
    faq: [
      {
        q: 'What is the maximum AC rebate available under VEU in Victoria?',
        a: 'The maximum rebate under the VEU program for reverse-cycle air conditioning is up to $7,000 for large ducted systems replacing gas heating. The exact amount depends on the system size, the appliance being replaced, and prevailing VEEC prices at the time of installation.',
      },
      {
        q: 'Can I replace an older split system and still get a VEU rebate?',
        a: 'Yes, in many cases. If your existing split system is an older, less efficient model, you may be eligible for a VEU rebate when upgrading to a new, high-efficiency reverse-cycle system. Eligibility depends on the specific existing model. Book a free assessment to confirm.',
      },
      {
        q: 'Does the reverse-cycle AC rebate cover installation costs?',
        a: 'Yes. The VEU rebate covers both the supply of the new unit and installation. You pay a single discounted price that reflects the total value after the government rebate is applied.',
      },
      {
        q: 'Is reverse-cycle air conditioning suitable for heating in Victoria?',
        a: 'Absolutely. Modern reverse-cycle systems are highly effective for heating Victorian homes. They are the most energy-efficient form of electric heating available and are rated to operate in temperatures well below zero — more than sufficient for Melbourne and regional Victoria winters.',
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 2 — Heat Pump Hot Water Rebate
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'heat-pump-hot-water-rebate-victoria',
    title: 'Heat Pump Hot Water Rebate Victoria 2026: How Much Can You Save?',
    date: '2026-04-16',
    readTime: '6 min read',
    category: 'Heat Pump Hot Water',
    description: 'Victorian homeowners can access a government VEU rebate on heat pump hot water systems — reducing hot water bills by up to 70%. Find out how much you can save and how to claim.',
    keywords: 'heat pump hot water rebate Victoria, heat pump VEU rebate, heat pump hot water system Victoria, hot water rebate Melbourne, heat pump water heater government rebate Victoria, replace gas hot water heat pump Victoria',
    intro: 'Your hot water system is typically the second-largest energy consumer in a Victorian home — responsible for around 25% of total household energy use. Replacing an inefficient gas or electric hot water system with a heat pump could cut your hot water energy bills by up to 70%, and right now Victorian homeowners can access a significant government rebate to help make the switch.',
    sections: [
      {
        heading: 'What Is a Heat Pump Hot Water System?',
        paragraphs: [
          'A heat pump hot water system works on the same principle as a reverse-cycle air conditioner — but instead of heating (or cooling) your home, it heats your water. It extracts heat energy from the surrounding air and uses it to heat water stored in an insulated tank, consuming far less electricity than a conventional electric resistance hot water system.',
          'Because it moves heat rather than generating it, a heat pump typically delivers 3–4 units of hot water energy for every 1 unit of electricity consumed — making it 300–400% efficient.',
        ],
      },
      {
        heading: 'How Much Is the VEU Heat Pump Rebate in Victoria?',
        paragraphs: [
          'Under the Victorian Energy Upgrades (VEU) program, eligible heat pump hot water installations attract a government rebate that substantially reduces — and in many cases eliminates — the upfront cost. The rebate is applied as a direct discount on installation day, so you never need to claim money back.',
          'The exact rebate value depends on the tank capacity of the system, the type of existing hot water being replaced (gas or electric), and current VEEC prices. Our team provides an exact figure during your free assessment.',
        ],
      },
      {
        heading: 'Gas vs Electric: Which Gets the Better Rebate?',
        paragraphs: [
          'Households replacing a gas hot water system with a heat pump typically attract a higher VEU rebate than those replacing an electric storage system. This reflects the larger environmental benefit of moving away from gas — a significant source of household carbon emissions.',
          'If you currently have gas hot water, switching to a heat pump is one of the most impactful changes you can make to both your energy bills and your home\'s carbon footprint — and the VEU program makes it financially accessible.',
        ],
        tip: 'Replacing a gas storage hot water system with a heat pump can save a typical Victorian household $400–$900 per year on energy bills, depending on usage.',
      },
      {
        heading: 'How Much Can You Save on Hot Water Bills?',
        paragraphs: [
          'Heat pump hot water systems are significantly more efficient than the alternatives:',
        ],
        list: {
          items: [
            'Compared to electric resistance (storage): 60–70% reduction in hot water energy use',
            'Compared to gas storage: 50–65% reduction in energy cost (depends on gas vs electricity prices)',
            'Compared to continuous flow (instantaneous) gas: 40–60% reduction',
          ],
          ordered: false,
        },
      },
      {
        heading: 'Installation: What to Expect',
        paragraphs: [
          'Heat pump hot water installations are typically completed in 3–5 hours by a licensed plumber working under Movement Green\'s VEU accreditation. The installation process involves:',
        ],
        list: {
          items: [
            'Removal and safe disposal of the old hot water system',
            'Installation of the new heat pump unit in a suitable outdoor or well-ventilated location',
            'Connection of plumbing and electrical supply',
            'System testing and sign-off',
            'Briefing you on the optimal settings for your new system',
          ],
          ordered: true,
        },
      },
      {
        heading: 'Things to Know Before You Install',
        paragraphs: [
          'Heat pump hot water systems work best when installed in a well-ventilated location where they can draw on ambient air. They are suitable for outdoor installation or in larger spaces like garages and utility rooms. They are not suitable for installation in small enclosed spaces without ventilation.',
          'All VEU-approved heat pump systems are designed to operate effectively in Victorian climate conditions, including colder winter months.',
        ],
        note: 'Heat pumps make a low operational sound similar to a reverse-cycle air conditioner outdoor unit. This should be considered when selecting the installation location.',
      },
    ],
    faq: [
      {
        q: 'Will my hot water work normally with a heat pump system?',
        a: 'Yes. A heat pump hot water system delivers the same hot water experience as your existing system — same pressure, same temperature, same capacity (based on tank size). The only difference is how the energy to heat the water is generated.',
      },
      {
        q: 'Can a heat pump hot water system handle a large family?',
        a: 'Yes. Heat pump systems are available in tank sizes from around 160 litres up to 315 litres and more. During your free assessment, we assess your household size and usage to recommend the most appropriate capacity.',
      },
      {
        q: 'Does a heat pump hot water system work in cold Victorian winters?',
        a: 'Yes. VEU-approved heat pump systems are rated to operate in temperatures down to around -7°C to -10°C, which is well below the minimum temperatures experienced anywhere in metropolitan Victoria. They do operate slightly less efficiently in very cold weather, but still outperform conventional electric systems.',
      },
      {
        q: 'How long does a heat pump hot water system last?',
        a: 'A well-maintained heat pump hot water system typically has a lifespan of 10–15 years. Most VEU-eligible systems come with a manufacturer\'s warranty of at least 5 years on the heat pump component.',
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 1 — What is the VEU Program (pillar content, published first)
  ───────────────────────────────────────────────────────────── */
  {
    slug: 'what-is-the-victorian-energy-upgrades-veu-program',
    title: 'What Is the Victorian Energy Upgrades (VEU) Program? A Complete 2026 Guide',
    date: '2026-04-15',
    readTime: '7 min read',
    category: 'VEU Program',
    description: 'Everything you need to know about the Victorian Energy Upgrades (VEU) program — what it is, how it works, what rebates are available, and how to access them.',
    keywords: 'Victorian Energy Upgrades program, what is VEU program, VEU rebates Victoria, how does VEU work, Victorian Energy Upgrades guide, VEU program explained, ESC Victoria energy upgrades',
    intro: 'The Victorian Energy Upgrades (VEU) program is one of Australia\'s largest and most accessible energy efficiency rebate programs — yet millions of eligible Victorian households have never accessed it. If you live in Victoria and have an older hot water system, air conditioner, or lighting, you could be missing out on hundreds — or even thousands — of dollars in government rebates.',
    sections: [
      {
        heading: 'What Is the Victorian Energy Upgrades Program?',
        paragraphs: [
          'The Victorian Energy Upgrades (VEU) program is a Victorian Government initiative administered by the Essential Services Commission (ESC) of Victoria. It was established under the Victorian Energy Efficiency Target (VEET) Act 2007 and has been operating continuously since 2009, making it one of the longest-running and most successful energy efficiency schemes in Australia.',
          'The program\'s goal is to reduce Victoria\'s greenhouse gas emissions by helping households and businesses replace inefficient appliances with energy-efficient alternatives — at significantly reduced cost.',
        ],
      },
      {
        heading: 'How Does the VEU Program Work?',
        paragraphs: [
          'The VEU program operates through a certificate trading mechanism. When an eligible energy-efficient appliance is installed in a Victorian home or business, an Accredited Person (AP) — a business accredited by the ESC — creates Victorian Energy Efficiency Certificates (VEECs) based on the energy savings the upgrade delivers over its lifetime.',
          'These VEECs are surrendered to Victorian energy retailers, who are required by law to meet annual VEEC surrender targets. The financial value of those certificates flows back to the customer as a discount on the installation price.',
          'In plain terms: energy retailers pay for part of your upgrade, and the discount is applied when your new appliance is installed. You never need to lodge a claim or wait for a rebate payment.',
        ],
      },
      {
        heading: 'What Upgrades Are Available Under VEU?',
        paragraphs: [
          'The VEU program covers a wide range of energy efficiency upgrades for Victorian homes and businesses. The most popular and highest-value upgrades currently include:',
        ],
        list: {
          items: [
            'Heat pump hot water systems — replacing gas or electric storage hot water',
            'Reverse-cycle air conditioners (split systems and ducted) — replacing gas heaters or older inefficient units',
            'LED lighting — replacing halogen downlights and fluorescent tubes',
            'Commercial solar — for eligible Victorian businesses',
          ],
          ordered: false,
        },
      },
      {
        heading: 'How Much Can You Save?',
        paragraphs: [
          'The savings under the VEU program are substantial. Here is a summary of the typical rebate values for the most popular upgrade types:',
        ],
        list: {
          items: [
            'Reverse-cycle air conditioner: up to $7,000 rebate',
            'Heat pump hot water (replacing gas): rebate often covers full installation cost',
            'LED lighting: rebate often covers full cost for residential properties',
            'Commercial solar: significant rebate for eligible Victorian businesses',
          ],
          ordered: false,
        },
      },
      {
        heading: 'Who Administers the VEU Program?',
        paragraphs: [
          'The VEU program is administered by the Essential Services Commission (ESC) of Victoria. The ESC accredits businesses as Accredited Persons (APs), maintains the approved product list, sets VEEC surrender targets for energy retailers, and enforces compliance across the scheme.',
          'Sustainability Victoria is the Victorian Government agency that promotes the program to households and businesses, providing consumer information and resources.',
          'Movement Green is a VEU Accredited Person, accredited by the ESC. We help Victorian homeowners identify every upgrade their home qualifies for, install the upgrades, and apply the VEU rebate directly to the invoice.',
        ],
      },
      {
        heading: 'Who Is Eligible for VEU Rebates?',
        paragraphs: [
          'The VEU program is open to:',
        ],
        list: {
          items: [
            'Victorian homeowners (owner-occupiers)',
            'Landlords and property investors with Victorian rental properties',
            'Renters (with landlord consent)',
            'Victorian businesses, including commercial, retail, and hospitality',
            'Community organisations and schools',
          ],
          ordered: false,
        },
      },
      {
        heading: 'How Do I Access VEU Rebates?',
        paragraphs: [
          'The simplest way to access VEU rebates is to book a free home energy assessment with Movement Green. Our team visits your property, identifies every upgrade your home qualifies for, and manages the entire process — from product selection to installation. As a VEU Accredited Provider, we create the certificates and apply the rebate ourselves.',
          'You do not need to contact the ESC directly, fill in government forms, or deal with VEEC paperwork. We handle everything. On installation day, your invoice simply shows the full product price and the VEU rebate applied — and you pay the discounted amount.',
        ],
      },
      {
        heading: 'Is the VEU Program Still Running in 2026?',
        paragraphs: [
          'Yes. The VEU program is ongoing and continues to be one of the most active energy efficiency schemes in Australia. The Victorian Government has committed to the program\'s continuation, and the list of eligible products is periodically updated to reflect advances in energy-efficient technology.',
          'Rebate values fluctuate with the VEEC market price, so it is worth acting sooner rather than later to lock in current rebate levels.',
        ],
        tip: 'VEEC prices can change. The sooner you book your assessment and lock in your upgrade, the more certain your rebate value.',
      },
    ],
    faq: [
      {
        q: 'How long has the VEU program been running in Victoria?',
        a: 'The Victorian Energy Upgrades program has been operating since 2009 under the Victorian Energy Efficiency Target Act. It is one of the longest-running and most established energy efficiency rebate schemes in Australia, with millions of upgrades delivered to Victorian homes and businesses.',
      },
      {
        q: 'Is the VEU program the same as the solar rebate?',
        a: 'No. The VEU program is separate from solar and battery rebates. The VEU program covers energy efficiency upgrades — heat pumps, reverse-cycle AC, LED lighting, and commercial solar. Residential solar and solar battery storage are covered by separate Victorian and federal schemes (Solar Homes Program and SRES). You may be eligible for both sets of programs.',
      },
      {
        q: 'Do I have to pay the rebate back?',
        a: 'No. The VEU rebate is not a loan — it is a genuine discount funded by energy retailers\' VEEC obligations. Once applied, the rebate does not need to be repaid under any circumstances.',
      },
      {
        q: 'Can I get a VEU rebate if I have already had the appliance installed?',
        a: 'Generally, no. VEU rebates must be applied at the time of installation — retrospective claims are not possible under the scheme. If you are planning an upgrade, contact Movement Green first to ensure the installation is coordinated through the VEU program.',
      },
    ],
  },
];

/* ── Helper: get post by slug ────────────────────────────── */
export function getPostBySlug(slug) {
  return POSTS.find(p => p.slug === slug) || null;
}

/* ── Helper: get recent posts (exclude current) ──────────── */
export function getRelatedPosts(currentSlug, count = 3) {
  return POSTS.filter(p => p.slug !== currentSlug).slice(0, count);
}
